import { useState, useEffect } from "react";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ErrorMessage from "../../components/common/ErrorMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues } from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import { BackForeColor } from "../../components/common/BackForeColor";
import {
    AdditionalStatusCodeEnum,
    getAdditionalCostStatusName,
    getAdditionalCostStatusBackColor,
    getAdditionalCostStatusForeColor,
    getSupplyRequestFilteringOptions,
} from "../../common/EntitiesManagement";
import { SUPPLY_USAGE_API } from "../../services/config";
import SupplyRequestStatusChange from "./components/SupplyRequestStatusChange";

export default function SupplyRequestList() {
    const {
        config,
        supplyRequestData,
        pageIndexSupplyRequest,
        setPageIndexSupplyRequest,
        setBasicFilterOptions,
    } = useGlobalContext();

    useEffect(() => {
        setBasicFilterOptions(getSupplyRequestFilteringOptions());
    }, []);

    const normalizedData = Array.isArray(supplyRequestData)
        ? { Data: supplyRequestData, Count: supplyRequestData.length }
        : supplyRequestData;

    const [errorMessage, setErrorMessage] = useState("");
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [supplyUsageEditionModel, setSupplyUsageEditionModel] = useState(null);
    const [showStatusChangeModal, setShowStatusChangeModal] = useState(false);

    const getColumnTitles = () => [
        localize.code,
        localize.supply_label,
        localize.quantity,
        localize.measurement_unit,
        // localize.warehouse,
        localize.status,
        localize.observations,
        localize.request_date,
        localize.modified_by,
    ];

    const handleStatusChangeResult = (operationResult) => {
        if (operationResult?.result) {
            mutate([
                SUPPLY_USAGE_API,
                {
                    ...config,
                    headers: {
                        ...config.headers,
                        pagination: pageIndexSupplyRequest,
                    },
                },
            ]);
            window.location.reload(false);
        } else if (operationResult?.data) {
            setErrorMessage(operationResult.data);
            setShowErrorModal(true);
        }
        setShowStatusChangeModal(false);
    };

    return (
        <div className="lateral_padding">
            {showErrorModal && (
                <ErrorMessage
                    message={errorMessage}
                    title={localize.error_unexpected}
                    showModal={showErrorModal}
                    setShowModal={setShowErrorModal}
                />
            )}

            {showStatusChangeModal && supplyUsageEditionModel && (
                <SupplyRequestStatusChange
                    show={showStatusChangeModal}
                    setShow={setShowStatusChangeModal}
                    supplyUsageModel={supplyUsageEditionModel}
                    onResult={handleStatusChangeResult}
                    eventId={supplyUsageEditionModel?.EventId}
                />
            )}

            <Card
                variant="outlined"
                style={{
                    margin: "16px",
                    boxShadow:
                        "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
                }}
            >
                <CardContent>
                    <PageTitleSubtitle
                        title={localize.supply_request_title}
                        subtitle={localize.supply_request_description}
                    />
                    <SearcherAndAdd hideAddButton />
                </CardContent>
            </Card>

            <TableComponent
                pagination={
                    <PagePagination
                        pageIndex={pageIndexSupplyRequest}
                        setPageIndex={setPageIndexSupplyRequest}
                        dataArray={normalizedData}
                    />
                }
                firstColumnTitle={localize.actions}
                columnTitles={getColumnTitles()}
                results={
                    normalizedData?.Data?.length > 0
                        ? normalizedData.Data.map((item) => ({
                            key: item.Id,
                            data: [
                                <div>
                                    <ActionButtons
                                        hideDeleteButton
                                        detailsButtonText={localize.supply_request_view}
                                        onClickForDetailButton={() => {
                                            window.open(
                                                `/events/detail/${item.eventId}?tab_id=5`,
                                                "_blank"
                                            );
                                        }}
                                        hideEditButton={
                                            item.Status !== AdditionalStatusCodeEnum.InProgress
                                        }
                                        onClickForEditButton={() => {
                                            setSupplyUsageEditionModel({
                                                ...item,
                                                SelectedStatus: {
                                                    Id: item.Status,
                                                    Name: getAdditionalCostStatusName(item.Status),
                                                },
                                            });
                                            setShowStatusChangeModal(true);
                                        }}
                                    />
                                </div>,
                                <ValueOrNoDataLabel data={item?.SupplyData?.Code} />,
                                <ValueOrNoDataLabel
                                    data={item?.SupplyData?.Name}
                                />,
                                item?.Quantity ?? 0,
                                <ValueOrNoDataLabel
                                    data={
                                        item?.SupplyData?.Unit?.Symbol
                                            ? `${item?.SupplyData?.Unit?.Name} (${item?.SupplyData?.Unit?.Symbol})`
                                            : item?.SupplyData?.Unit?.Name
                                    }
                                />,
                                //<ValueOrNoDataLabel data={item?.Warehouse?.Name} />,
                                <BackForeColor
                                    backColor={getAdditionalCostStatusBackColor(item.Status)}
                                    foreColor={getAdditionalCostStatusForeColor(item.Status)}
                                    text={getAdditionalCostStatusName(item.Status)}
                                />,
                                <ValueOrNoDataLabel
                                    data={
                                        item?.Observation || item?.StatusObservation
                                            ? `${item?.Observation ?? ""} ${item?.StatusObservation ?? ""}`.trim()
                                            : undefined
                                    }
                                />,
                                new Date(item.CreationDate).toLocaleString(undefined, {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                }),
                                <ValueOrNoDataLabel data={item?.CreatedBy} />,
                            ],
                        }))
                        : [generateNoDataValues(9)]
                }
            />
        </div>
    );
}
